import PlayLogo from '@ui-components/PlayLogo';
import FocusTrap from 'focus-trap-react';
import { animate } from 'motion';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SwitchTransition, Transition } from 'react-transition-group';
import {
    StyledCookieConsent,
    StyledCookieConsentActions,
    StyledCookieConsentBalk,
    StyledCookieConsentLogo,
    StyledCookieConsentSettings,
    StyledCookieContainer,
} from './CookieConsent.styles';
import CookieConsentButtons from './CookieConsentButtons/CookieConsentButtons';
import CookieConsentConfirm from './CookieConsentConfirm/CookieConsentConfirm';
import CookieContentInfo from './CookieConsentInfo/CookieContentInfo';
import { CookieConsentContext } from './CookieConsentProvider';
import { CookieConsentSettings } from './CookieConsentSetting/CookieConsentSettings';
import { StyledCookieConsentBackButton } from './CookieSetting/CookieSetting.styles';
import { useCookieConsent } from './hooks/useCookieConsent';

export enum SubPage {
    Info = 'info',
    Settings = 'settings',
    Confirm = 'confirm',
}

export enum Variant {
    Original = 'Original',
    OneLayer = 'OneLayer',
    OneLayerRejectAll = 'OneLayerRejectAll',
    TwoLayersRejectAll = 'TwoLayersRejectAll',
    TwoLayersNoRejectAll = 'TwoLayersNoRejectAll',
}

const Page = ({ currentPage, onChecked, isOptedIn, variant, expanded }) => {
    switch (currentPage) {
        case SubPage.Info:
            return <CookieContentInfo />;
        case SubPage.Confirm:
            return <CookieConsentConfirm />;
        default:
            return (
                <CookieConsentSettings
                    onChecked={onChecked}
                    isOptedIn={isOptedIn}
                    variant={variant}
                    expanded={expanded}
                />
            );
    }
};

const CookieConsent = ({ variant }: { variant: Variant }) => {
    const [currentPage, setCurrentPage, save, acceptAll, rejectAll, onChecked, isOptedIn, showConsentModal] =
        useCookieConsent();

    const modalRef = useRef(null);
    const pageRef = useRef(null);

    const { setVariant } = useContext(CookieConsentContext);
    const [mounted, setMounted] = useState(false);

    // Set variant based on Abisko allocation (loads asynchronous)
    useEffect(() => {
        setVariant(variant);
    }, [variant]);

    // Don't render on server side
    useEffect(() => {
        setMounted(true);
    });

    // If ESC was pressed, a user might be able to tab to
    // content outside the modal. This effect prevents that.
    useEffect(() => {
        const onFocus = () => {
            modalRef.current?.focus();
        };

        window.addEventListener('focus', onFocus);

        return () => {
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    // Resets focus when a user navigate through the pages
    useEffect(() => {
        modalRef.current?.focus();
    }, [currentPage]);

    // Page transition
    useEffect(() => {
        if (pageRef?.current) {
            animate(pageRef.current, { opacity: [null, 0, null, 1] }, { duration: 0.3 });
        }
    }, [currentPage]);

    if (variant === Variant.Original) {
        return null;
    }

    if (!mounted) {
        return null;
    }

    if (!showConsentModal.visible) {
        return null;
    }

    return (
        <FocusTrap focusTrapOptions={{ initialFocus: modalRef.current }}>
            <StyledCookieContainer>
                <StyledCookieConsent
                    role="dialog"
                    aria-modal="true"
                    aria-label="Val av cookies på svtplay.se"
                    aria-describedby="cookie-desc"
                    tabIndex={0}
                    ref={modalRef}
                    small={currentPage === SubPage.Info}
                    smallMaxHeight={currentPage === SubPage.Confirm}
                    tallHeight={currentPage === SubPage.Settings && variant === Variant.OneLayer}
                >
                    <StyledCookieConsentSettings centerContent={currentPage === SubPage.Confirm}>
                        <StyledCookieConsentBalk isHidden={currentPage === SubPage.Confirm}>
                            {currentPage === SubPage.Settings &&
                            variant !== Variant.OneLayer &&
                            variant !== Variant.OneLayerRejectAll ? (
                                <StyledCookieConsentBackButton
                                    onClick={() => {
                                        setCurrentPage(SubPage.Info);
                                    }}
                                >
                                    <span>Tillbaka</span>
                                </StyledCookieConsentBackButton>
                            ) : (
                                <span />
                            )}
                            <StyledCookieConsentLogo isHidden={currentPage === SubPage.Confirm}>
                                <PlayLogo />
                            </StyledCookieConsentLogo>
                        </StyledCookieConsentBalk>
                        <div ref={pageRef}>
                            <SwitchTransition>
                                <Transition key={currentPage} timeout={100}>
                                    <Page
                                        currentPage={currentPage}
                                        isOptedIn={isOptedIn}
                                        onChecked={onChecked}
                                        variant={variant}
                                        expanded={showConsentModal.expanded}
                                    />
                                </Transition>
                            </SwitchTransition>
                        </div>
                    </StyledCookieConsentSettings>
                    {currentPage !== SubPage.Confirm && (
                        <StyledCookieConsentActions>
                            <CookieConsentButtons
                                onSave={(e) => {
                                    e.preventDefault();
                                    save();
                                }}
                                onAcceptAll={(e) => {
                                    e.preventDefault();
                                    acceptAll();
                                }}
                                setCurrentPage={setCurrentPage}
                                onRejectAll={(e) => {
                                    e.preventDefault();
                                    rejectAll();
                                }}
                                currentPage={currentPage}
                                variant={variant}
                            />
                        </StyledCookieConsentActions>
                    )}
                </StyledCookieConsent>
            </StyledCookieContainer>
        </FocusTrap>
    );
};

export default CookieConsent;
